import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";

import {
  RelationshipTree,
  type RelationshipTreeNode,
} from "@/components/cinch/RelationshipTree";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCan } from "@/lib/auth/useCan";

import { useRoster, type CompanyOption, type RequestAction, type RosterParent } from "../api";
import { AIReviewCard } from "./AIReviewCard";
import { ChangelogPanel } from "./ChangelogPanel";
import { RelationshipDialog } from "./RelationshipDialog";
import { RequestsPanel } from "./RequestsPanel";

/**
 * Expanded view: the visual relationship tree (stacked levels included), the
 * AI review block, this company's approval queue, and its slice of the change
 * log. The tree is assembled client-side from the roster — every parent's
 * children are known, so a child that is itself a parent recurses.
 */
export function CompanyDetailContent({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const roster = useRoster();
  const canWrite = useCan("parent_companies.write");
  const [dialog, setDialog] = useState<{
    mode: RequestAction;
    parent?: CompanyOption | null;
    child?: CompanyOption | null;
  } | null>(null);

  const byId = useMemo(() => {
    const m = new Map<string, RosterParent>();
    for (const p of roster.data?.parents ?? []) m.set(p.id, p);
    return m;
  }, [roster.data]);

  const parent = byId.get(companyId);

  const tree = useMemo<RelationshipTreeNode | null>(() => {
    if (!parent) return null;
    const build = (id: string, name: string, visited: Set<string>): RelationshipTreeNode => {
      const node = byId.get(id);
      const isRoot = id === parent.id;
      const chips = (
        <>
          {node && !isRoot && (
            <StatusBadge value="parent" label="Parent · Child" />
          )}
          {node?.ai_status === "flagged" && <StatusBadge value="flagged" />}
        </>
      );
      const meta = node
        ? [node.brand_name, node.account_name, [node.city, node.state].filter(Boolean).join(", ")]
            .filter(Boolean)
            .join(" · ")
        : undefined;
      // Guard against accidental cycles — Cinch only blocks self-links.
      if (visited.has(id)) {
        return { id, name: `${name} (cycle)`, muted: true };
      }
      const nextVisited = new Set(visited).add(id);
      return {
        id,
        name,
        meta,
        badges: chips,
        muted: node?.is_active === false,
        children: node?.children.map((c) => {
          const childNode = byId.get(c.id);
          if (childNode) return build(c.id, c.name, nextVisited);
          return {
            id: c.id,
            name: c.name,
            muted: c.is_active === false,
            badges: c.is_active === false ? <StatusBadge value="inactive" /> : undefined,
          };
        }),
      };
    };
    return build(parent.id, parent.name, new Set());
  }, [parent, byId]);

  if (roster.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }
  if (!parent || !tree) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        This company has no children in Cinch.
      </p>
    );
  }

  const asOption = (id: string, name: string): CompanyOption => ({
    id,
    name,
    is_active: null,
    account_name: null,
  });

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold">Relationship tree</h3>
          <span className="text-xs text-muted-foreground">
            {parent.descendant_count} companies under {parent.name}
            {parent.stacked ? " · stacked" : ""}
          </span>
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() =>
                setDialog({ mode: "add", parent: asOption(parent.id, parent.name) })
              }
            >
              Propose adding a child…
            </Button>
          )}
        </div>
        <Card>
          <CardContent className="p-4">
            <RelationshipTree
              root={tree}
              selectedId={companyId}
              onNodeClick={(node) => {
                // Clicking another parent in the tree pivots the drawer to it.
                if (node.id !== companyId && byId.has(node.id)) {
                  navigate({
                    to: "/parent-companies",
                    search: { company: node.id, expanded: true },
                  });
                }
              }}
            />
          </CardContent>
        </Card>
        {canWrite && (
          <p className="text-xs text-muted-foreground">
            To edit a specific link, use Move / Unlink on the child rows in the
            quick view, or propose a change below — everything routes through a
            second-user approval.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <AIReviewCard parentId={parent.id} parentName={parent.name} />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-base font-semibold">Change requests</h3>
        <RequestsPanel companyId={companyId} />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-base font-semibold">Change log</h3>
        <ChangelogPanel companyId={companyId} />
      </section>

      {dialog && (
        <RelationshipDialog
          open
          onOpenChange={(v) => {
            if (!v) setDialog(null);
          }}
          mode={dialog.mode}
          parent={dialog.parent}
          child={dialog.child}
        />
      )}
    </div>
  );
}
